import { AttachableDocument, RelatedEntityType } from "@dune/types";
import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	ResizableLayout,
} from "@dune/ui";
import { useMemo, useState } from "react";
import { usePropositionDocuments } from "../../../../api/queries/propositions.queries";
import { TemplateSelectionDialog } from "../../../../components/email/TemplateSelectionDialog";
import SignatureEditorDialog from "../../../../email/SignatureEditorDialog";
import { EmailComposerForm } from "../../../../email/components/EmailComposerForm";
import { useEmailComposer } from "../../../../hooks/useEmailComposer";
import { useImageUpload } from "../../../../hooks/useImageUpload";
import { usePropositionTemplateData } from "../../../../hooks/usePropositionTemplateData";
import type { EmailComposerPanelProps } from "../../../../types/panel.types";

export const PropositionEmailComposerPanel = ({
	initialData,
	draftId,
	disabled = false,
	replyTo,
	entityId,
	entityType = "PROPOSITION",
	onClose,
}: EmailComposerPanelProps) => {
	const { uploadImage } = useImageUpload();
	const [showDebugInfo, setShowDebugInfo] = useState(false);

	const { getDebugInfo } = usePropositionTemplateData({
		propositionId: entityId,
	});

	const {
		control,
		watch,
		attachments,
		showSaveDraftPrompt,
		setShowSaveDraftPrompt,
		showCcBcc,
		setShowCcBcc,
		showTemplateDialog,
		setShowTemplateDialog,
		isSignatureDialogOpen,
		setIsSignatureDialogOpen,
		toSearch,
		ccSearch,
		bccSearch,
		templates,
		isLoadingTemplates,
		handleTemplateSelection,
		handleAttachmentAdd,
		handleAttachmentRemove,
		handleExistingDocumentsAdd,
		handleSendEmail,
		handleSaveDraft,
		handleDiscardDraft,
		getHandleEmailSearch,
		sendEmailMutation,
	} = useEmailComposer({
		initialData,
		draftId,
		entityId,
		entityType: RelatedEntityType.PROPOSITION,
		replyTo,
		onClose,
		mapAttachmentToPayload: (att) => ({
			propositionDocumentId: att.entityDocumentId,
		}),
	});

	const { data: propositionDocuments, isLoading: isLoadingDocuments } =
		usePropositionDocuments(entityType === "PROPOSITION" ? entityId : 0);

	const propositionDocumentsAsAttachableDocuments: AttachableDocument[] =
		useMemo(() => {
			if (!propositionDocuments) return [];
			return propositionDocuments.map(
				(doc): AttachableDocument => ({
					id: doc.id.toString(),
					name: doc.file.name,
					bucket: "private",
					key: doc.file.key,
					mimeType: doc.file.mimeType,
					type: "OTHER",
					createdAt: doc.createdAt,
					updatedAt: doc.updatedAt,
					status: "VALIDATED",
				}),
			);
		}, [propositionDocuments]);

	const handlePropositionDocumentsAdd = async (
		documents: AttachableDocument[],
	) => {
		await handleExistingDocumentsAdd({
			documents,
			originalDocuments: propositionDocuments,
			findOriginal: (docs, id) => docs.find((doc) => doc.id.toString() === id),
			extractInfo: (doc) => ({
				bucket: "private",
				key: doc.file.key,
				name: doc.file.name,
				mimeType: doc.file.mimeType,
			}),
			extractId: (doc) => doc.id.toString(),
			entityName: "proposition",
		});
	};

	const renderDebugInfo = () => {
		if (!showDebugInfo || !import.meta.env.DEV) return null;

		const debugInfo = getDebugInfo();
		if (!debugInfo) {
			return (
				<div className="p-3 bg-gray-50 rounded-lg border text-xs">
					<p className="text-gray-500">Aucune donnée chargée</p>
				</div>
			);
		}

		return (
			<div className="p-3 bg-gray-50 rounded-lg border text-xs">
				<h4 className="font-medium mb-2">🔍 Debug - Données disponibles:</h4>
				<div className="space-y-2">
					<div>
						<strong>Placeholders disponibles:</strong>
						<div className="bg-white p-2 rounded border mt-1 max-h-32 overflow-y-auto">
							{debugInfo.availablePlaceholders.map((placeholder) => (
								<div key={placeholder} className="text-green-600 font-mono">
									{placeholder}
								</div>
							))}
						</div>
					</div>
					<div>
						<strong>Structure des données:</strong>
						<pre className="bg-white p-2 rounded border mt-1 max-h-40 overflow-y-auto text-[10px]">
							{debugInfo.dataStructure}
						</pre>
					</div>
				</div>
			</div>
		);
	};

	return (
		<>
			<ResizableLayout.PanelContent className="p-4">
				<EmailComposerForm
					control={control}
					watch={watch}
					disabled={disabled}
					attachments={attachments}
					showCcBcc={showCcBcc}
					setShowCcBcc={setShowCcBcc}
					toSearch={toSearch}
					ccSearch={ccSearch}
					bccSearch={bccSearch}
					getHandleEmailSearch={getHandleEmailSearch}
					handleAttachmentAdd={handleAttachmentAdd}
					handleAttachmentRemove={handleAttachmentRemove}
					existingDocuments={propositionDocumentsAsAttachableDocuments}
					isLoadingDocuments={isLoadingDocuments}
					onExistingDocumentsAdd={handlePropositionDocumentsAdd}
					onImageUpload={uploadImage}
					onTemplateButtonClick={() => setShowTemplateDialog(true)}
					onSignatureButtonClick={() => setIsSignatureDialogOpen(true)}
					onSendClick={handleSendEmail}
					sendEmailMutation={sendEmailMutation}
					isLoadingTemplates={isLoadingTemplates}
					renderExtraContent={
						import.meta.env.DEV
							? () => (
									<>
										{renderDebugInfo()}
										<Button
											variant="outline"
											size="sm"
											onClick={() => setShowDebugInfo(!showDebugInfo)}
											className="ml-2"
										>
											🔍 {showDebugInfo ? "Masquer" : "Afficher"} debug
										</Button>
									</>
								)
							: undefined
					}
				/>
			</ResizableLayout.PanelContent>

			<Dialog
				open={showSaveDraftPrompt}
				onOpenChange={(open: boolean) => {
					if (!open) {
						setShowSaveDraftPrompt(false);
					}
				}}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Sauvegarder le brouillon ?</DialogTitle>
						<DialogDescription>
							Voulez-vous sauvegarder ce message en tant que brouillon ?
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="outline" onClick={handleDiscardDraft}>
							Supprimer
						</Button>
						<Button onClick={handleSaveDraft}>Sauvegarder</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<SignatureEditorDialog
				open={isSignatureDialogOpen}
				onOpenChange={setIsSignatureDialogOpen}
			/>

			<TemplateSelectionDialog
				open={showTemplateDialog}
				onOpenChange={setShowTemplateDialog}
				templates={templates || []}
				isLoading={isLoadingTemplates}
				onSelectTemplate={handleTemplateSelection}
			/>
		</>
	);
};
